import React, { useMemo } from 'react'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Box from '@mui/material/Box'
import { FieldType } from '../../utils/fieldTypeEnum'
import PopOverInfo from '../../PopOverInfo'
import usePropStyles from '../../utils/usePropStyles'
import ErrorMessage from '../../ErrorMessage'
import { useController } from 'react-hook-form'
import { useTheme } from '@mui/material/styles'
import { InputFieldBase } from '../types'

export interface SelectProps extends InputFieldBase {
  items?: IMenuItem[]
}

export interface IMenuItem {
  label: string
  value: string
}

export const Select: React.FC<SelectProps> = ({
  fieldType,
  fieldWidth,
  assistiveText,
  items,
  popover,
  control,
  required,
  disabled,
  ...props
}) => {
  const fieldName = props.name!
  const { field, fieldState } = useController({ name: fieldName, control, rules: { required: required! } })
  const hasError = Boolean(fieldState.error)

  if (fieldType !== FieldType.SELECT) {
    throw Error(`Select field expected fieldtype of SELECT, instead got ${fieldType}`)
  }
  if (!items) {
    throw Error(`Items cannot be null or undefined`)
  }
  if (items.length <= 0) {
    throw Error(`Items cannot be an empty array`)
  }

  const propStyles = usePropStyles(fieldWidth)

  const { palette } = useTheme()
  const styles = useMemo(
    () => ({
      boxStyles: {
        display: 'flex',
        paddingBottom: '24px',
        '& > button': {
          alignSelf: 'baseline',
        },
      },
      successIcon: {
        color: palette.success.main,
        width: '20px',
        height: '20px',
      },
    }),
    [],
  )

  return (
    <Box sx={styles.boxStyles}>
      <TextField
        {...props}
        {...field}
        sx={propStyles}
        select
        disabled={disabled!}
        id={fieldName!}
        error={hasError}
        helperText={
          hasError ? (
            <ErrorMessage>
              <>{fieldState.error?.message}</>
            </ErrorMessage>
          ) : (
            assistiveText
          )
        }
        InputLabelProps={{ required: false }}
      >
        {items.map((item, i) => {
          return (
            <MenuItem key={`${field.name}-option${i}-${item.value}`} value={item.value}>
              {item.label}
            </MenuItem>
          )
        })}
      </TextField>
      {popover && <PopOverInfo {...popover} />}
    </Box>
  )
}
